import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  People,
  Work,
  Business,
  ArrowForward,
  Group,
  Assignment,
  PersonAdd,
} from '@mui/icons-material';
import userService from '../../services/user.service';
import employeeService from '../../services/employee.service';
import departmentService from '../../services/department.service';

const OrganizationOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalEmployees: 0,
    totalDepartments: 0,
    activeEmployees: 0,
    recentEmployees: [] as any[],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [usersResponse, employeesResponse, departmentsResponse, activeEmployeesResponse, recentEmployeesResponse] = await Promise.all([
          userService.getUserProfiles({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          employeeService.getAll({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          departmentService.getDepartments({ 'pagination[pageSize]': 1 }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          employeeService.getAll({ 'pagination[pageSize]': 1, 'filters[status][$eq]': 'active' }).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
          employeeService.getAll({ 'pagination[pageSize]': 5, 'sort[0]': 'createdAt:desc' }).catch(() => ({ data: { data: [] } })),
        ]);

        setStats({
          totalUsers: usersResponse.data?.meta?.pagination?.total || 0,
          totalEmployees: employeesResponse.data?.meta?.pagination?.total || 0,
          totalDepartments: departmentsResponse.data?.meta?.pagination?.total || 0,
          activeEmployees: activeEmployeesResponse.data?.meta?.pagination?.total || 0,
          recentEmployees: recentEmployeesResponse.data?.data || [],
        });
      } catch (error) {
        console.error('Error fetching organization data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const StatCard = ({ title, value, icon, color, onClick }: any) => (
    <Card elevation={2} sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 6 } : {} }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>{value}</Typography>
        <Typography color="text.secondary" variant="body2">{title}</Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Organization Overview</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>{t('organization.overview')}</Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
          <StatCard title={t('organization.totalUsers')} value={stats.totalUsers} icon={<People />} color="primary.main" onClick={() => navigate('/users')} />
          <StatCard title={t('organization.totalEmployees')} value={stats.totalEmployees} icon={<Group />} color="info.main" onClick={() => navigate('/employees')} />
          <StatCard title={t('organization.activeEmployees')} value={stats.activeEmployees} icon={<PersonAdd />} color="success.main" onClick={() => navigate('/employees')} />
          <StatCard title={t('organization.departments')} value={stats.totalDepartments} icon={<Business />} color="secondary.main" onClick={() => navigate('/departments')} />
        </Box>

        <Card elevation={2} sx={{ mb: 4 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight="bold">{t('organization.recentEmployees')}</Typography>
            <IconButton size="small" onClick={() => navigate('/employees')}><ArrowForward /></IconButton>
          </Box>
          <CardContent>
            <List>
              {stats.recentEmployees.length > 0 ? (
                stats.recentEmployees.map((employee: any, index: number) => (
                  <React.Fragment key={employee.id}>
                    <ListItem sx={{ px: 0, cursor: 'pointer' }} onClick={() => navigate(`/employees/${employee.id}`)}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body1">{employee.attributes?.firstName} {employee.attributes?.lastName}</Typography>
                            <Chip label={employee.attributes?.status || 'active'} size="small" color={employee.attributes?.status === 'active' ? 'success' : 'default'} />
                          </Box>
                        }
                        secondary={employee.attributes?.position || 'N/A'}
                      />
                    </ListItem>
                    {index < stats.recentEmployees.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <ListItem><ListItemText primary={t('common.noData')} /></ListItem>
              )}
            </List>
          </CardContent>
        </Card>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/users')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6">{t('organization.users')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/roles')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Assignment sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
              <Typography variant="h6">{t('organization.rolesPermissions')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/employees')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Work sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h6">{t('organization.hrManagement')}</Typography>
            </CardContent>
          </Card>
          <Card elevation={2} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} onClick={() => navigate('/departments')}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Business sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
              <Typography variant="h6">{t('organization.departments')}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
  );
};

export default OrganizationOverview;

